import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  DraftBodyResult,
  LetterPatch,
  LetterStatus,
  LinePatch,
  PriceLetterDetail,
  PriceLettersResponse,
  StandardParagraph,
  UploadResult,
} from './letterTypes'

const ROOT = '/api/price-change-letters'

export function letterPdfUrl(letterId: string, download = false): string {
  const base = import.meta.env.VITE_API_BASE ?? ''
  return `${base}${ROOT}/${letterId}/pdf${download ? '?download=true' : ''}`
}

export function usePriceLetters(filters: { partnerId?: number | null; status?: LetterStatus | null }) {
  const params = new URLSearchParams()
  if (filters.partnerId) params.set('partner_id', String(filters.partnerId))
  if (filters.status) params.set('status', filters.status)
  const qs = params.toString()
  return useQuery({
    queryKey: ['price-letters', 'list', filters.partnerId ?? null, filters.status ?? null],
    queryFn: () => jsonFetch<PriceLettersResponse>(`${ROOT}${qs ? `?${qs}` : ''}`),
    staleTime: 10_000,
  })
}

export function usePriceLetter(letterId: string | null) {
  return useQuery({
    queryKey: ['price-letters', 'detail', letterId],
    queryFn: () => jsonFetch<PriceLetterDetail>(`${ROOT}/${letterId}`),
    enabled: letterId !== null,
  })
}

export function useStandardParagraphs(letterId: string | null, effectiveDate: string | null) {
  return useQuery({
    queryKey: ['price-letters', 'paragraphs', letterId, effectiveDate],
    queryFn: () =>
      jsonFetch<{ paragraphs: StandardParagraph[] }>(
        `${ROOT}/standard-paragraphs${letterId ? `?letter_id=${letterId}` : ''}`,
      ),
    enabled: letterId !== null,
    staleTime: 60_000,
  })
}

export function useUploadPriceWorkbook() {
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      return jsonFetch<UploadResult>(`${ROOT}/upload`, { method: 'POST', rawBody: fd })
    },
  })
}

export function useCreateLetter() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { document_id: number; sheet_index: number; partner_id: number }) =>
      jsonFetch<PriceLetterDetail>(ROOT, { method: 'POST', body: vars }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['price-letters', 'list'] }),
  })
}

function useDetailMutation<V>(fn: (vars: V) => Promise<PriceLetterDetail>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (data) => {
      qc.setQueryData(['price-letters', 'detail', data.letter.id], data)
      void qc.invalidateQueries({ queryKey: ['price-letters', 'list'] })
    },
  })
}

export function usePatchLetter() {
  return useDetailMutation((v: { letterId: string; changes: LetterPatch }) =>
    jsonFetch<PriceLetterDetail>(`${ROOT}/${v.letterId}`, { method: 'PATCH', body: v.changes }),
  )
}

export function usePatchLetterLine() {
  return useDetailMutation((v: { letterId: string; lineId: number; changes: LinePatch }) =>
    jsonFetch<PriceLetterDetail>(`${ROOT}/${v.letterId}/lines/${v.lineId}`, {
      method: 'PATCH',
      body: v.changes,
    }),
  )
}

export function useFinalizeLetter() {
  return useDetailMutation((letterId: string) =>
    jsonFetch<PriceLetterDetail>(`${ROOT}/${letterId}/finalize`, { method: 'POST' }),
  )
}

export function useAttachLetter() {
  return useDetailMutation((letterId: string) =>
    jsonFetch<PriceLetterDetail>(`${ROOT}/${letterId}/attach`, { method: 'POST' }),
  )
}

export function useDraftLetterBody() {
  return useMutation({
    mutationFn: (letterId: string) =>
      jsonFetch<DraftBodyResult>(`${ROOT}/${letterId}/draft-body`, { method: 'POST' }),
  })
}

export function useDeleteLetter() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (letterId: string) => jsonFetch<{ ok: true }>(`${ROOT}/${letterId}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['price-letters', 'list'] }),
  })
}
